import type { ParseKeys } from 'i18next'

import { ArrowCounterClockwise } from '#design-system/icons/arrow-counter-clockwise'
import { Hexagon } from '#design-system/icons/hexagon'
import { MoneyWavy } from '#design-system/icons/money-wavy'
import { Package } from '#design-system/icons/package'
import { ShoppingCart } from '#design-system/icons/shopping-cart'
import { StackSimple } from '#design-system/icons/stack-simple'
import { Truck } from '#design-system/icons/truck'
import type { NavApp } from '~/shell/regions/Nav/AppNav'
import type { NavSectionConfig } from '~/shell/regions/Nav/Nav'

export interface SettingsNavItem {
  id: string
  labelKey: ParseKeys
}

export interface SettingsNavGroup {
  titleKey: ParseKeys
  items: SettingsNavItem[]
}

const getRowStatus = (i: number) => {
  if (i % 3 === 0) {
    return 'Active'
  }
  if (i % 3 === 1) {
    return 'Pending'
  }
  return 'Inactive'
}

export const SHELL_TEST_NAV_ITEMS = Array.from({ length: 40 }, (_, i) => ({
  id: i + 1,
  label: `Nav item ${String(i + 1)}`,
  path: `/`,
}))

export const SHELL_TEST_COLUMNS = Array.from({ length: 20 }, (_, i) => `Column ${String(i + 1)}`)

export const SHELL_TEST_ROWS = Array.from({ length: 60 }, (_, i) => ({
  id: i + 1,
  name: `Row item ${String(i + 1)}`,
  status: getRowStatus(i),
  value: `$${((i + 1) * 137.42).toFixed(2)}`,
}))

export const SHELL_TEST_NAV_FLYOUT = {
  notificationCount: 3,
  tenant: {
    id: 'thursday-boots',
    initials: 'TB',
    name: 'Thursday Boots',
  },
  memberCount: 12,
  member: {
    name: 'Member name',
    role: 'Role',
    email: 'member@example.com',
  },
  workspaceTree: [
    {
      id: 'daily-boots',
      initials: 'DB',
      name: 'Daily Boots',
      children: [
        {
          id: 'sunday-boots',
          initials: 'SU',
          name: 'Sunday Boots',
        },
        {
          id: 'weekday-boots',
          initials: 'WK',
          name: 'Weekday Boots',
          children: [
            { id: 'thursday-boots', initials: 'TB', name: 'Thursday Boots', isActive: true },
            { id: 'wednesday-boots', initials: 'WB', name: 'Wednesday Boots' },
            { id: 'friday-boots', initials: 'FB', name: 'Friday Boots' },
          ],
        },
        {
          id: 'weekend-boots',
          initials: 'WE',
          name: 'Weekend Boots',
          children: [{ id: 'saturday-boots', initials: 'SB', name: 'Saturday Boots' }],
        },
      ],
    },
  ],
}

export const SHELL_TEST_NAV_SECTIONS: NavSectionConfig[] = [
  {
    // Home renders the app → page → sub-page tree (SHELL_TEST_NAV_APP_TREE), not flat children.
    path: '/home',
    section: 'home',
    labelKey: 'shell.nav.home',
    children: [],
  },
  {
    path: '/inbox',
    section: 'inbox',
    labelKey: 'shell.nav.inbox',
    children: [],
  },
  {
    path: '/chats',
    section: 'chats',
    labelKey: 'shell.nav.chats',
    children: [
      { path: '/chats/channel-1', labelKey: 'test.pages.chats.channel_1' },
      { path: '/chats/channel-2', labelKey: 'test.pages.chats.channel_2' },
    ],
  },
]

// Home-tab app → page → sub-page tree (placement data). Typed as NavApp[] so the icon invariant is
// enforced: apps + pages require an icon, sub-pages can't have one.
export const SHELL_TEST_NAV_APP_TREE: NavApp[] = [
  {
    id: 'order-management',
    name: 'Order management',
    icon: ShoppingCart,
    children: [
      {
        id: 'oms-dtc',
        name: 'OMS-DTC',
        icon: Hexagon,
        children: [
          { id: 'oms-dtc-dashboard', name: 'Dashboard' },
          { id: 'oms-dtc-all-orders', name: 'All orders' },
          { id: 'oms-dtc-pending', name: 'Pending' },
          { id: 'oms-dtc-shipped', name: 'Shipped' },
        ],
      },
      { id: 'oms-b2b', name: 'OMS-B2B', icon: Hexagon },
      { id: 'oms-seconds', name: 'OMS-Seconds', icon: Hexagon },
    ],
  },
  {
    id: 'fulfillment',
    name: 'Fulfillment',
    icon: Package,
    children: [
      {
        id: 'wms-dtc',
        name: 'WMS-DTC',
        icon: Hexagon,
        children: [
          { id: 'wms-dtc-dashboard', name: 'Dashboard' },
          { id: 'wms-dtc-pick-pack', name: 'Pick & pack' },
          { id: 'wms-dtc-wave-planning', name: 'Wave planning' },
          { id: 'wms-dtc-packing-stations', name: 'Packing stations' },
          { id: 'wms-dtc-shipments', name: 'Shipments' },
        ],
      },
      {
        id: 'wms-wholesale',
        name: 'WMS-Wholesale',
        icon: Hexagon,
        children: [
          { id: 'wms-ws-dashboard', name: 'Dashboard' },
          { id: 'wms-ws-pick-pack', name: 'Pick & pack' },
          { id: 'wms-ws-pallet-builds', name: 'Pallet builds' },
          { id: 'wms-ws-shipments', name: 'Shipments' },
        ],
      },
      { id: 'carriers', name: 'Carriers', icon: Hexagon },
    ],
  },
  {
    id: 'inventory',
    name: 'Inventory',
    icon: StackSimple,
    children: [
      { id: 'ims-warehouse', name: 'IMS-Warehouse', icon: Hexagon },
      { id: 'ims-retail', name: 'IMS-Retail', icon: Hexagon },
      { id: 'catalog', name: 'Catalog', icon: Hexagon },
    ],
  },
  { id: 'procurement', name: 'Procurement', icon: Truck },
  { id: 'returns', name: 'Returns', icon: ArrowCounterClockwise },
  { id: 'finance', name: 'Finance', icon: MoneyWavy },
]

export const SHELL_SETTINGS_NAV_GROUPS: SettingsNavGroup[] = [
  {
    titleKey: 'shell.nav.settings_nav.group.workspace',
    items: [
      { id: 'general', labelKey: 'shell.nav.settings_nav.item.general' },
      { id: 'branding', labelKey: 'shell.nav.settings_nav.item.branding' },
      { id: 'notifications', labelKey: 'shell.nav.settings_nav.item.notifications' },
    ],
  },
  {
    titleKey: 'shell.nav.settings_nav.group.platform',
    items: [
      { id: 'apps', labelKey: 'shell.nav.settings_nav.item.apps' },
      { id: 'data_sources', labelKey: 'shell.nav.settings_nav.item.data_sources' },
      { id: 'workflows', labelKey: 'shell.nav.settings_nav.item.workflows' },
      { id: 'forms', labelKey: 'shell.nav.settings_nav.item.forms' },
      { id: 'layouts', labelKey: 'shell.nav.settings_nav.item.layouts' },
      { id: 'integrations', labelKey: 'shell.nav.settings_nav.item.integrations' },
      { id: 'api_keys', labelKey: 'shell.nav.settings_nav.item.api_keys' },
      { id: 'webhooks', labelKey: 'shell.nav.settings_nav.item.webhooks' },
    ],
  },
  {
    titleKey: 'shell.nav.settings_nav.group.people',
    items: [
      { id: 'members', labelKey: 'shell.nav.settings_nav.item.members' },
      { id: 'member_groups', labelKey: 'shell.nav.settings_nav.item.member_groups' },
      { id: 'roles_permissions', labelKey: 'shell.nav.settings_nav.item.roles_permissions' },
      { id: 'provisioning', labelKey: 'shell.nav.settings_nav.item.provisioning' },
    ],
  },
  {
    titleKey: 'shell.nav.settings_nav.group.observability',
    items: [
      { id: 'activity_feed', labelKey: 'shell.nav.settings_nav.item.activity_feed' },
      { id: 'data_explorer', labelKey: 'shell.nav.settings_nav.item.data_explorer' },
      { id: 'audit_log', labelKey: 'shell.nav.settings_nav.item.audit_log' },
    ],
  },
  {
    titleKey: 'shell.nav.settings_nav.group.billing',
    items: [
      { id: 'plans', labelKey: 'shell.nav.settings_nav.item.plans' },
      { id: 'invoices', labelKey: 'shell.nav.settings_nav.item.invoices' },
      { id: 'payment_methods', labelKey: 'shell.nav.settings_nav.item.payment_methods' },
    ],
  },
  {
    titleKey: 'shell.nav.settings_nav.group.security',
    items: [
      { id: 'sso', labelKey: 'shell.nav.settings_nav.item.sso' },
      { id: 'two_factor', labelKey: 'shell.nav.settings_nav.item.two_factor' },
      { id: 'session_policy', labelKey: 'shell.nav.settings_nav.item.session_policy' },
      { id: 'ip_allowlist', labelKey: 'shell.nav.settings_nav.item.ip_allowlist' },
    ],
  },
]
